import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngxs/store';
import { QuoteState } from '@nibtravel/shared/data-access/quote';
import { DomService } from '@nibtravel/shared/util/dom';
import {
  ActionFieldObject,
  CheckoutStep,
  EcommerceProduct,
  EcommerceService,
  EventType,
  PlanProduct,
} from '@nibtravel/shared/util/gtm';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, take, first } from 'rxjs/operators';
import { ItemAnalyticsService } from './item-analytics.service';
import { OptionsAnalyticService } from './options-analytics.service';
import { PlanAnlyticsService } from './plan-analytics.service';
import { PurchasePath } from './purchase-path.selectors';
import { set } from 'lodash-es';
import { PurchasePathAnalyticsUtilsService } from './utils/purchasePathAnalyticsUtils.service';

@Injectable()
export class PurchasePathAnalyticsService implements OnDestroy {
  destroy$ = new Subject();

  constructor(
    private store: Store,
    private ecommerce: EcommerceService,
    private planService: PlanAnlyticsService,
    private optionsService: OptionsAnalyticService,
    private itemService: ItemAnalyticsService,
    private domService: DomService,
    private analyticsCommon: PurchasePathAnalyticsUtilsService
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  purchase(): void {
    const policyNumber = this.store.selectSnapshot(QuoteState.policyNumber);
    const price = this.store.selectSnapshot(PurchasePath.price);
    const campaignCode = this.store.selectSnapshot(PurchasePath.campaignCode);
    const action: ActionFieldObject = {
      id: `${policyNumber}`,
      revenue: price?.totalPrice,
      coupon: `${campaignCode}`,
      action: EventType.Purchase,
    };

    this.domService.addToDataLayer({
      purchaseData: [
        { [EventType.QuotePrice]: `${price?.totalPrice}` },
        { [EventType.PolicyNumber]: policyNumber },
      ],
    });

    this.getProducts()
      .pipe(take(1))
      .subscribe((products: EcommerceProduct[]) => {
        this.ecommerce.purchase(products, action);
      });
  }

  checkout(step: CheckoutStep): void {
    this.getProducts()
      .pipe(take(1))
      .subscribe((products: EcommerceProduct[]) => {
        this.ecommerce.checkout(products, {
          id: `${step}`,
          step: step,
        });
      });
  }

  private getProducts(): Observable<EcommerceProduct[]> {
    return combineLatest([
      this.planService.getProduct(),
      this.optionsService.getProducts(),
      this.itemService.getProducts(),
    ]).pipe(
      map(
        ([planProduct, optionProducts, itemProducts]) =>
          [planProduct, ...optionProducts, ...itemProducts].filter(
            (p) => !!p
          ) as EcommerceProduct[]
      )
    );
  }

  submitQuote(data: any): void {
    const {
      residence,
      destination,
      destination_id,
      destination_group,
      date_departure,
      date_return,
      travellers,
      t_1_age,
      t_2_age,
      t_ages,
    } = this.analyticsCommon.getAnalyticsCommonData();
    const formData = {
      form_type: 'form_submit',
      location: 'Homepage',
      form_id: 'form_submit',
      form_name: 'TID AU Purchase Path Get a Quote form',
      residence,
      destination,
      destination_id,
      destination_group,
      date_departure,
      date_return,
      travellers,
      t_1_age,
      t_2_age,
      t_ages,
      submission_success: true,
      title: 'Get a Quote',
    };
    if (data?.errors?.length > 0) {
      set(formData, 'errors', data.errors);
      set(formData, 'submission_success', false);
    }
    this.domService.pushDataToGA4(EventType.FormSubmit, formData);
  }

  emailQuote(data: any): void {
    const {
      residence,
      destination,
      destination_id,
      destination_group,
      date_departure,
      date_return,
      travellers,
      t_1_age,
      t_2_age,
      t_ages,
    } = this.analyticsCommon.getAnalyticsCommonData();
    const formData = {
      form_type: 'form_submit',
      location: 'Quote',
      form_step_name: 'email_quote',
      title: 'email_quote',
      form_id: 'EmailQuote',
      form_name: 'quoteEmail',
      residence,
      destination,
      destination_id,
      destination_group,
      date_departure,
      date_return,
      travellers,
      t_1_age,
      t_2_age,
      t_ages,
      submission_success: true,
      health_member: 'false',
    };
    if (data?.errors?.length > 0) {
      set(formData, 'errors', data.errors);
      set(formData, 'submission_success', false);
    }
    this.domService.pushDataToGA4(EventType.QuoteEmail, formData);
  }

  submitPayment(data: any): void {
    const {
      residence,
      destination,
      destination_id,
      destination_group,
      date_departure,
      date_return,
      travellers,
      t_1_age,
      t_2_age,
      t_ages,
    } = this.analyticsCommon.getAnalyticsCommonData();

    const {
      hv_sports_gear,
      hv_tech_camera,
      hv_personal_items,
      sport_activity,
      program_name,
      program_id,
      program_amount,
    } = this.analyticsCommon.getAnalyticAdditionalPolicyData();

    const formData = {
      form_type: 'form_submit',
      location: 'payment',
      form_step_name: 'quote_payment',
      form_step_number: '5',
      title: 'submit payment',
      form_id: 'quote_payment',
      form_name: 'TID AU Purchase Path Submit Payment Form',
      residence,
      destination,
      destination_id,
      destination_group,
      date_departure,
      date_return,
      travellers,
      t_1_age,
      t_2_age,
      t_ages,
      submission_success: true,
      health_member: 'false',
      hv_sports_gear,
      hv_tech_camera,
      hv_personal_items,
      sport_activity,
      program_name,
      program_id,
      program_amount,
    };
    if (data?.errors?.length > 0) {
      set(formData, 'errors', data.errors);
      set(formData, 'submission_success', false);
    }
    this.domService.pushDataToGA4(EventType.SubmitPayment, formData);
  }

  editTripEvent() {
    const {
      residence,
      destination,
      destination_id,
      destination_group,
      date_departure,
      date_return,
      travellers,
      t_1_age,
      t_2_age,
      t_ages,
    } = this.analyticsCommon.getAnalyticsCommonData();
    const formData = {
      event: 'interaction',
      interaction_type: 'button',
      location: window.location.href,
      form_step_name: 'Edit trip form',
      form_step_number: '1',
      title: 'Edit trip form',
      accordion_open: 'false',
      form_id: 'edit_trip',
      form_name: 'TID AU Purchase Path - Edit trip form',
      destination,
      residence,
      destination_id,
      destination_group,
      date_departure,
      date_return,
      travellers,
      t_1_age,
      t_2_age,
      t_ages,
      health_member: 'false',
    };
    this.domService.pushDataToGA4(EventType.EditTrip, formData);
  }

  updateQuoteEvent(data: any): void {
    const {
      residence,
      destination,
      destination_id,
      destination_group,
      date_departure,
      date_return,
      travellers,
      t_1_age,
      t_2_age,
      t_ages,
    } = this.analyticsCommon.getAnalyticsCommonData();
    const formData = {
      form_type: 'step_submit',
      location: window.location.href,
      form_step_name: 'Update a Quote',
      form_step_number: '1',
      title: 'Update a Quote',
      form_id: 'update_quote',
      form_name: 'TID AU Purchase Path Update a Quote',
      destination,
      residence,
      destination_id,
      destination_group,
      date_departure,
      date_return,
      travellers,
      t_1_age,
      t_2_age,
      t_ages,
      health_member: 'false',
      submission_success: true,
      error: 'false',
    };
    if (data?.errors?.length > 0) {
      set(formData, 'errors', data.errors);
      set(formData, 'submission_success', false);
    }
    this.domService.pushDataToGA4(EventType.UpdateQuote, formData);
  }

  applyPromotionEvent() {
    const {
      residence,
      destination,
      destination_id,
      destination_group,
      date_departure,
      date_return,
      travellers,
      t_1_age,
      t_2_age,
      t_ages,
    } = this.analyticsCommon.getAnalyticsCommonData();
    const formData = {
      event: 'form',
      form_type: 'step_submit',
      location: 'Quote Page',
      form_step_name: 'Apply promotion',
      form_step_number: '1',
      title: 'Apply promotion',
      accordion_open: 'false',
      form_id: 'apply_promotion',
      form_name: 'TID AU Purchase Path - Apply Promotion',
      destination,
      residence,
      destination_id,
      destination_group,
      date_departure,
      date_return,
      travellers,
      t_1_age,
      t_2_age,
      t_ages,
      health_member: 'false',
      submission_success: 'true',
      error: 'false',
    };
    this.domService.pushDataToGA4(EventType.ApplyPromotion, formData);
  }

  selectPlan(data: any) {
    return this.planService
      .getProduct()
      .pipe(first())
      .subscribe((product) => {
        const {
          residence,
          destination,
          destination_id,
          destination_group,
          date_departure,
          date_return,
          travellers,
          t_1_age,
          t_2_age,
          t_ages,
        } = this.analyticsCommon.getAnalyticsCommonData();
        const formData = {
          plan_id: data?.planId,
          plan_name: product?.name,
          plan_price: product?.price,
          plan_currency: product?.currencyCode,
          form_type: 'step_submit',
          location: 'Quote Form',
          form_id: 'quote_form',
          form_name: 'Quote Form',
          form_step_name: 'Quote Form',
          form_step_number: 1,
          residence,
          destination,
          destination_id,
          destination_group,
          date_departure,
          date_return,
          travellers,
          t_1_age,
          t_2_age,
          t_ages,
          submission_success: true,
          title: 'Choose your cover',
          error: false,
          health_member: false,
        };
        if (data?.errors?.length > 0) {
          set(formData, 'errors', data.errors);
          set(formData, 'submission_success', false);
        }
        this.domService.pushDataToGA4(EventType.Form, formData);
      });
  }

  togglePlanDetailsAccordion(data: any) {
    const {
      residence,
      destination,
      destination_id,
      destination_group,
      date_departure,
      date_return,
      travellers,
      t_1_age,
      t_2_age,
      t_ages,
    } = this.analyticsCommon.getAnalyticsCommonData();
    const formData = {
      location: 'Quote Form',
      form_id: 'quote_form',
      form_name: 'Quote Form',
      form_step_name: 'Quote Form',
      form_step_number: 1,
      residence,
      destination,
      destination_id,
      destination_group,
      date_departure,
      date_return,
      travellers,
      t_1_age,
      t_2_age,
      t_ages,
      submission_success: true,
      interaction_type: 'accordion',
      accordion_open: data?.data?.accordionOpen,
      title: data?.data?.title,
      error: false,
      health_member: false,
    };
    if (data?.data?.errors?.length > 0) {
      set(formData, 'errors', data.errors);
      set(formData, 'submission_success', false);
    }
    this.domService.pushDataToGA4(EventType.Interaction, formData);
  }
}
